import { useCallback, useEffect, useState } from "react";

import { StartStreamTranscriptionCommand } from "@aws-sdk/client-transcribe-streaming";
import { Buffer } from "buffer";
import MicrophoneStream from "microphone-stream";
// @ts-ignore
import process from "process/browser";

import { transcribeClient } from "app/transcribe-client";

import { MAX_AUDIO_CHUNK_SIZE, pcmEncodeChunk } from "./speech-to-text.utils";

window.Buffer = window.Buffer || Buffer;
window.process = window.process || process;

type useSpeechToTextProps = {
  isRecording: boolean;
  onAppendText: (text: string) => void;
  enabled?: boolean;
};

export default function useSpeechToText({
  isRecording,
  onAppendText,
  enabled = true
}: useSpeechToTextProps) {
  const [micStream, setMicStream] = useState<MicrophoneStream | null>(null);

  const startRecord = useCallback(async () => {
    try {
      const microphoneStream = new MicrophoneStream();
      microphoneStream.setStream(
        await window.navigator.mediaDevices.getUserMedia({
          video: false,
          audio: true
        })
      );
      setMicStream(microphoneStream);
      // Acquire the microphone audio stream.
      const audioStream = async function* () {
        // @ts-ignore
        for await (const chunk of microphoneStream) {
          if (chunk.length <= MAX_AUDIO_CHUNK_SIZE) {
            yield {
              AudioEvent: {
                AudioChunk: pcmEncodeChunk(chunk)
              }
            };
          }
        }
      };

      const command = new StartStreamTranscriptionCommand({
        // The language code for the input audio. Valid values are en-GB, en-US, es-US, fr-CA, and fr-FR.
        LanguageCode: "en-US",
        // The encoding used for the input audio. The only valid value is pcm.
        MediaEncoding: "pcm",
        // The sample rate of the input audio in Hertz.
        MediaSampleRateHertz: 44100,
        AudioStream: audioStream()
      });

      // Send the speech stream to Amazon Transcribe.
      const data = await transcribeClient.send(command);
      // @ts-ignore
      for await (const event of data.TranscriptResultStream) {
        // @ts-ignore
        for (const result of event.TranscriptEvent.Transcript.Results || []) {
          if (result.IsPartial === false) {
            // @ts-ignore
            const noOfResults = result.Alternatives[0].Items.length;
            // Print results to browser window.
            const output = [];
            for (let i = 0; i < noOfResults; i++) {
              // @ts-ignore
              output.push(result.Alternatives[0].Items[i].Content);
            }
            onAppendText(
              output
                .join(" ")
                .replaceAll(" .", ".")
                .replaceAll(" ,", ",")
                .replaceAll(" ?", "?")
                .replaceAll(" !", "!")
            );
          }
        }
      }
      transcribeClient.destroy();
    } catch (err) {
      console.log("Error. ", err);
    }
  }, [onAppendText]);
  useEffect(() => {
    // Early return if disabled - prevents any recording activity
    if (!enabled) {
      // If disabled while recording, stop any active recording
      if (micStream) {
        micStream.stop();
        setMicStream(null);
      }
      return;
    }

    if (isRecording) {
      if (!micStream) {
        startRecord();
      }
    } else {
      if (micStream) {
        micStream.stop();
        setMicStream(null);
      }
    }

    return () => {
      if (micStream) {
        micStream.stop();
        setMicStream(null);
      }
    };
  }, [enabled, isRecording, micStream, startRecord]);

  return null;
}
